import React, { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGroups } from '../../../../../redux/groups/hooks/useGroups';
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../../../../redux/store.model';
import { getPolyglot } from '../../../../../i18n';
import {
  getGroupByGroupIdInWorkspaceSelector,
  getGroupSelectedByQueryParamSelector,
} from '../../../../../redux/groups/selectors';
import {
  getDevicesNotRestrictedByQueryParamSelector,
  getMyWorkspaceSelector,
} from '../../../../../redux/stoerkID/selectors/StoerkId.selectors';
import { useAllDevices } from '../../../../../redux/commander/hooks/useAllDevices';
import { getRightUserUtilSelector } from '../../../../../redux/rights/selectors';
import { useShowErrorMessage } from '../../../../../handlingErrors';
import { getFeatureToggle } from '../../../../../featureToggle';
import { GroupManager } from '../../../../../components/DeviceManager/Group/Overview/GroupManager/GroupManager';
import {
  isSCCCustomer,
  isSCCService,
} from '../../../redux/rights/selectors/roles';
import { useGroupsByWorkspaceId } from '../../../../../redux/groups/hooks/useGroupByWorkspace';
import { useGroupManagerData } from '../../../hooks/useGroupManagerData';
import { CompanyIcon, StoreIcon } from '../../../theme/icons';
import { NormalizedGroup } from '../../../../../redux/groups/api/GroupAdapter';
import { useTheme } from '@mui/material';

const FreshwatchGroupManagerFunctional = () => {
  const featureToggle = useAppSelector(getFeatureToggle);
  const params = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const polyglot = getPolyglot();
  const workspace = useAppSelector(getMyWorkspaceSelector);
  const isSccCustomer = useAppSelector(isSCCCustomer);
  const isSccService = useAppSelector(isSCCService);
  const theme = useTheme();
  const { data: groups, isLoading: loadingGroups } = useGroups(params.groupId);

  const group = useAppSelector((state) =>
    getGroupSelectedByQueryParamSelector(state, { params })
  );

  const devices = useAppSelector((state) =>
    getDevicesNotRestrictedByQueryParamSelector(state, { params })
  );
  const { isLoading: loadingDevicesFromGroup } = useAllDevices();

  const rightsUserUtil = useAppSelector(getRightUserUtilSelector);
  const showErrorMessage = useShowErrorMessage();
  const dispatch = useAppDispatch();

  const groupFromWorkspace = useAppSelector((state: RootState) =>
    getGroupByGroupIdInWorkspaceSelector(state, {
      groupId: params.groupId,
      workspaceId: workspace?.id ?? '',
    })
  );

  const { data: companiesGroups = {}, isLoading: loadingCompaniesGroups } =
    useGroupsByWorkspaceId(workspace?.id || '');
  // Use custom hook to manage group filtering and loading
  const { storeGroups, companyGroups, childStoreGroups, isLoadingChildGroups } =
    useGroupManagerData({
      isSccCustomer: isSccCustomer || isSccService,
      groups,
      companiesGroups,
    });

  // Format groups with icons based on context
  const filteredGroups = useMemo(() => {
    if (isSccCustomer || isSccService) {
      // Store users see their stores
      return storeGroups.map((group: NormalizedGroup) => ({
        ...group,
        iconNode: (
          <StoreIcon
            style={{ color: theme.palette.primary.main, height: '2.5rem' }}
          />
        ),
      }));
    }

    if (params.groupId) {
      // Company users viewing a specific company see its stores
      return childStoreGroups.map((group: NormalizedGroup) => ({
        ...group,
        iconNode: (
          <StoreIcon
            style={{ color: theme.palette.primary.main, height: '2.5rem' }}
          />
        ),
      }));
    }

    // Company users at root see all companies
    return companyGroups.map((group: NormalizedGroup) => ({
      ...group,
      iconNode: (
        <CompanyIcon
          style={{ color: theme.palette.primary.main, height: '2.5rem' }}
        />
      ),
    }));
  }, [
    isSccCustomer,
    storeGroups,
    companyGroups,
    childStoreGroups,
    params.groupId,
    theme.palette.primary.main,
  ]);

  if (isSccCustomer && !params.groupId) {
    return null;
  }
  return (
    <GroupManager
      featureToggle={featureToggle}
      groups={filteredGroups}
      loadingGroups={
        loadingGroups || loadingCompaniesGroups || isLoadingChildGroups
      }
      group={group ?? groupFromWorkspace}
      devices={devices}
      loadingDevices={loadingDevicesFromGroup}
      navigate={navigate}
      location={location}
      params={params}
      polyglot={polyglot}
      rightsUserUtil={rightsUserUtil}
      handlingErrorsApi={showErrorMessage}
      dispatch={dispatch}
      workspaceId={workspace?.id ?? ''}
    />
  );
};

export default FreshwatchGroupManagerFunctional;
